// src/config.ts
import { createContext, useContext } from "react";
var StorageConfigContext = createContext(null);
var useStorageConfig = () => {
  const ctx = useContext(StorageConfigContext);
  if (!ctx) throw new Error("StorageProvider missing");
  return ctx;
};

// src/StorageProvider.tsx
import { createContext as createContext2, useContext as useContext2, useEffect, useMemo, useState } from "react";
import { useAuth } from "@coremind/identity-core";

// src/path.ts
function buildPaths(platform, tenantId, cognitoSub) {
  const base = `${platform}/tenants/${tenantId}/users/${cognitoSub}/`;
  return {
    baseUserPrefix: base,
    privatePrefix: `${base}private/`,
    publicPrefix: `${base}public/`,
    sharedPrefix: `${platform}/tenants/${tenantId}/shared/`
  };
}
function joinKey(prefix, ...parts) {
  return `${prefix}${parts.filter(Boolean).join("/")}`.replace(/\/+/g, "/");
}

// src/creds.ts
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
var b64urlToB64 = (s) => s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
function assertLoginsKeyMatchesIdToken(loginsKey, idToken) {
  const mKey = /cognito-idp\.([a-z0-9-]+)\.amazonaws\.com\/([^/]+)/.exec(loginsKey);
  if (!mKey) throw new Error(`Invalid loginsKey: ${loginsKey}`);
  const [, region, userPoolId] = mKey;
  const [, payloadB64] = idToken.split(".");
  if (!payloadB64) throw new Error("Malformed ID token");
  const payload = JSON.parse(atob(b64urlToB64(payloadB64)));
  const iss = payload.iss;
  const mIss = /https:\/\/cognito-idp\.([a-z0-9-]+)\.amazonaws\.com\/([^/]+)/.exec(iss);
  if (!mIss) throw new Error(`ID token iss not from Cognito: ${iss}`);
  const [, tokenRegion, tokenUserPoolId] = mIss;
  if (region !== tokenRegion || userPoolId !== tokenUserPoolId) {
    throw new Error(
      `Logins/provider mismatch:
       loginsKey=${loginsKey}
       token.iss=${iss}
       (region ${region} vs ${tokenRegion}; userPoolId ${userPoolId} vs ${tokenUserPoolId})`
    );
  }
}
var LS_PREFIX = "cogIdentV1";
function lsKey(identityPoolId, userPoolId, sub) {
  return `${LS_PREFIX}:${identityPoolId}:${userPoolId}:${sub}`;
}
function readCachedCreds(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiration) return null;
    const expMs = new Date(parsed.expiration).getTime();
    if (expMs - Date.now() > 5 * 60 * 1e3) return parsed;
    return null;
  } catch {
    return null;
  }
}
function writeCachedCreds(key, creds) {
  try {
    const expiration = creds.expiration instanceof Date ? creds.expiration.toISOString() : new Date(Date.now() + 50 * 60 * 1e3).toISOString();
    const toStore = {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
      expiration
    };
    localStorage.setItem(key, JSON.stringify(toStore));
  } catch {
  }
}
function withLocalStorageCache(base, cacheKey) {
  let inflight2 = null;
  return async () => {
    const cached2 = readCachedCreds(cacheKey);
    if (cached2) {
      return {
        accessKeyId: cached2.accessKeyId,
        secretAccessKey: cached2.secretAccessKey,
        sessionToken: cached2.sessionToken
      };
    }
    if (!inflight2) {
      inflight2 = base().then((fresh) => {
        writeCachedCreds(cacheKey, fresh);
        return fresh;
      }).finally(() => {
        inflight2 = null;
      });
    }
    return inflight2;
  };
}
function makeIdentityCredentials(cfg, session) {
  if (!cfg.userPoolId) throw new Error("userPoolId missing in config");
  if (!session.idToken) throw new Error("idToken missing for authenticated identity");
  const userPoolRegion = cfg.userPoolRegion || cfg.region;
  const loginsKey = `cognito-idp.${userPoolRegion}.amazonaws.com/${cfg.userPoolId}`;
  assertLoginsKeyMatchesIdToken(loginsKey, session.idToken);
  const identityPoolRegion = cfg.identityPoolRegion || cfg.region;
  const base = fromCognitoIdentityPool({
    identityPoolId: cfg.identityPoolId,
    clientConfig: { region: identityPoolRegion },
    logins: { [loginsKey]: session.idToken }
  });
  const cacheKey = lsKey(cfg.identityPoolId, cfg.userPoolId, session.cognitoSub);
  return withLocalStorageCache(base, cacheKey);
}

// src/s3.ts
import {
  S3Client,
  PutObjectCommand,
  GetObjectCommand,
  DeleteObjectCommand,
  ListObjectsV2Command,
  ListMultipartUploadsCommand,
  AbortMultipartUploadCommand
} from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { RequestChecksumCalculation } from "@aws-sdk/middleware-flexible-checksums";
var MISSING_TTL_MS = 5 * 60 * 1e3;
var _missingKeyCache = /* @__PURE__ */ new Map();
function _markMissing(key, ttlMs = MISSING_TTL_MS) {
  _missingKeyCache.set(key, Date.now() + ttlMs);
}
function _isMissingCached(key) {
  const exp = _missingKeyCache.get(key);
  if (!exp) return false;
  if (Date.now() > exp) {
    _missingKeyCache.delete(key);
    return false;
  }
  return true;
}
function clearMissingKeyCache(key) {
  if (key) _missingKeyCache.delete(key);
  else _missingKeyCache.clear();
}
function makeS3Client(region, credentials) {
  return new S3Client({
    region,
    credentials,
    requestChecksumCalculation: RequestChecksumCalculation.WHEN_REQUIRED,
    // ↓ avoid internal retry amplification on 404s
    maxAttempts: 1
  });
}
async function putJson(s3, bucket, key, data, contentType = "application/json") {
  const body = new Blob([JSON.stringify(data)], { type: contentType });
  await s3.send(new PutObjectCommand({
    Bucket: bucket,
    Key: key,
    Body: body,
    ContentType: contentType,
    ServerSideEncryption: "AES256"
  }));
  clearMissingKeyCache(key);
}
async function putFileMultipart(s3, bucket, key, file, contentType, opts) {
  const { onProgress, signal, partSize, queueSize = 4 } = opts || {};
  const uploader = new Upload({
    client: s3,
    params: {
      Bucket: bucket,
      Key: key,
      Body: file,
      ContentType: contentType || file.type || "application/octet-stream",
      ServerSideEncryption: "AES256"
    },
    partSize,
    queueSize,
    leavePartsOnError: false
  });
  if (onProgress) {
    uploader.on("httpUploadProgress", (e) => {
      const loaded = e.loaded ?? 0;
      const total = e.total ?? file.size;
      const percent = total ? Math.round(loaded / total * 100) : void 0;
      onProgress({ key, loaded, total, percent, part: e.part });
    });
  }
  if (signal) {
    const abort = () => uploader.abort();
    if (signal.aborted) abort();
    signal.addEventListener("abort", abort, { once: true });
  }
  await uploader.done();
  clearMissingKeyCache(key);
}
async function getJson(s3, bucket, key) {
  const out = await s3.send(new GetObjectCommand({ Bucket: bucket, Key: key }));
  const text = await out.Body.transformToString();
  return JSON.parse(text);
}
async function getJsonViaCdn(publicBaseUrl, key) {
  const base = publicBaseUrl.replace(/\/+$/, "");
  const url = `${base}/${key.replace(/^\/+/, "")}`;
  const res = await fetch(url, { cache: "no-store", headers: { Accept: "application/json" } });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status} fetching ${url}`);
    err.status = res.status;
    throw err;
  }
  return await res.json();
}
async function getJsonOrNullViaCdn(publicBaseUrl, key, missTtlMs) {
  if (_isMissingCached(key)) return null;
  try {
    return await getJsonViaCdn(publicBaseUrl, key);
  } catch (err) {
    const status = err?.status;
    if (status === 404 || status === 403) {
      _markMissing(key, missTtlMs);
      return null;
    }
    throw err;
  }
}
async function getJsonOrNull(s3, bucket, key, missTtlMs) {
  if (_isMissingCached(key)) return null;
  try {
    return await getJson(s3, bucket, key);
  } catch (err) {
    const code = err?.name || err?.Code || err?.$metadata?.httpStatusCode;
    if (code === "NoSuchKey" || code === "NotFound" || code === 404) {
      _markMissing(key, missTtlMs);
      return null;
    }
    throw err;
  }
}
async function del(s3, bucket, key) {
  await s3.send(new DeleteObjectCommand({ Bucket: bucket, Key: key }));
  clearMissingKeyCache(key);
}
async function listPrefix(s3, bucket, prefix) {
  let ContinuationToken;
  const results = [];
  do {
    const out = await s3.send(new ListObjectsV2Command({ Bucket: bucket, Prefix: prefix, ContinuationToken }));
    results.push(...out.Contents ?? []);
    ContinuationToken = out.IsTruncated ? out.NextContinuationToken : void 0;
  } while (ContinuationToken);
  return results;
}
async function signGetUrl(s3, bucket, key, expiresInSec = 3600) {
  return getSignedUrl(s3, new GetObjectCommand({ Bucket: bucket, Key: key }), { expiresIn: expiresInSec });
}
async function abortAllMultipartForPrefix(s3, bucket, prefix) {
  const out = await s3.send(
    new ListMultipartUploadsCommand({ Bucket: bucket, Prefix: prefix })
  );
  for (const u of out.Uploads ?? []) {
    if (!u.Key || !u.UploadId) continue;
    await s3.send(
      new AbortMultipartUploadCommand({
        Bucket: bucket,
        Key: u.Key,
        UploadId: u.UploadId
      })
    );
  }
}
async function abortAllMultipartForKey(s3, bucket, key) {
  const out = await s3.send(
    new ListMultipartUploadsCommand({ Bucket: bucket, Prefix: key })
  );
  for (const u of out.Uploads ?? []) {
    if (u.Key === key && u.UploadId) {
      await s3.send(
        new AbortMultipartUploadCommand({
          Bucket: bucket,
          Key: key,
          UploadId: u.UploadId
        })
      );
    }
  }
}

// src/StorageProvider.tsx
import { jsx } from "react/jsx-runtime";
var resolveAppId = (p) => typeof p === "string" ? p : p.appId;
var StorageContext = createContext2(null);
var useStorage = () => {
  const v = useContext2(StorageContext);
  if (!v) throw new Error("useStorage must be used within <StorageProvider>");
  return v;
};
var _credsCacheKey = null;
var _credsCached = null;
var StorageProvider = ({
  config,
  children
}) => {
  const { session } = useAuth();
  const [tenantId, setTenantId] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [creds, setCreds] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const v = await Promise.resolve(config.getTenantId());
      if (alive) setTenantId(v);
    })();
    return () => {
      alive = false;
    };
  }, [config.getTenantId]);
  const idToken = useMemo(() => config.getIdToken?.() ?? null, [config.getIdToken, session?.user?.uuid]);
  useEffect(() => {
    let alive = true;
    const sub = session?.user?.uuid || null;
    if (!sub || !tenantId || !idToken) {
      if (alive) {
        setIdentity(null);
        setCreds(null);
      }
      return;
    }
    const key = `${config.identityPoolId}|${config.region}|${config.userPoolId}|${tenantId}|${sub}|${idToken.slice(0, 24)}`;
    if (_credsCacheKey !== key) {
      const ident = { tenantId, cognitoSub: sub, idToken };
      const c = makeIdentityCredentials(config, ident);
      _credsCacheKey = key;
      _credsCached = c;
      if (alive) {
        setIdentity(ident);
        setCreds(c);
      }
    } else {
      if (alive) {
        setIdentity({ tenantId, cognitoSub: sub, idToken });
        setCreds(_credsCached);
      }
    }
    return () => {
      alive = false;
    };
  }, [
    session?.user?.uuid,
    tenantId,
    idToken,
    config.identityPoolId,
    config.region,
    config.userPoolId
  ]);
  useEffect(() => {
    if (identity) {
      console.info("[StorageProvider] tenant:", identity.tenantId, "sub:", identity.cognitoSub);
    }
  }, [identity]);
  const s3 = useMemo(() => creds ? makeS3Client(config.region, creds) : null, [config.region, creds]);
  const paths = useMemo(
    () => identity ? buildPaths(
      resolveAppId(config.platform),
      // <-- pass string now
      identity.tenantId,
      identity.cognitoSub
    ) : null,
    [config.platform, identity]
  );
  const ready = Boolean(session?.user && creds && s3 && paths);
  const value = { cfg: config, identity, s3, paths, ready };
  return /* @__PURE__ */ jsx(StorageConfigContext.Provider, { value: config, children: /* @__PURE__ */ jsx(StorageContext.Provider, { value, children }) });
};

// src/StorageAppProvider.tsx
import { useMemo as useMemo4 } from "react";

// src/local/provider.tsx
import Dexie from "dexie";
import { createContext as createContext3, useContext as useContext3, useMemo as useMemo2 } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var LocalDB = class extends Dexie {
};
var LocalStorageContext = createContext3(null);
function LocalStorageProvider({
  schemas,
  dbName = "coremind-local",
  children
}) {
  const db = useMemo2(() => {
    const dexie = new LocalDB(dbName);
    dexie.version(1).stores({ ops: "++id,ts,ns,table,type,status" });
    for (const s of schemas) {
      const stores = {};
      for (const t of s.tables) stores[t.name] = t.indexes;
      dexie.version(s.version).stores(stores);
    }
    return dexie;
  }, [schemas, dbName]);
  const value = useMemo2(() => ({
    db,
    getTable(table) {
      return db.table(table);
    }
  }), [db]);
  return /* @__PURE__ */ jsx2(LocalStorageContext.Provider, { value, children });
}
function useLocalStorage() {
  const ctx = useContext3(LocalStorageContext);
  if (!ctx) throw new Error("useLocalStorage must be used within LocalStorageProvider");
  return ctx;
}

// src/local/sync.tsx
import { createContext as createContext4, useContext as useContext4, useCallback as useCallback2 } from "react";

// src/local/hooks.ts
import { useCallback } from "react";
function useLocalCollection(ns, tableName) {
  const { db, getTable } = useLocalStorage();
  const table = getTable(tableName);
  const list = useCallback(async (predicate) => {
    await db.open().catch(() => {
    });
    const rows = await table.toArray();
    return predicate ? rows.filter(predicate) : rows;
  }, [db, table]);
  const get = useCallback(async (key) => {
    await db.open().catch(() => {
    });
    return table.get(key);
  }, [db, table]);
  const put = useCallback(async (key, value, opType = "update") => {
    await db.transaction("rw", table, db.ops, async () => {
      await table.put(value);
      const op = { ts: Date.now(), ns, table: tableName, key, type: opType, payload: value, status: "pending" };
      await db.ops.add(op);
    });
  }, [db, table, ns, tableName]);
  const add = useCallback(async (value, key) => {
    const k = key ?? crypto.randomUUID();
    await put(k, value, "create");
    return k;
  }, [put]);
  const del2 = useCallback(async (key) => {
    await db.transaction("rw", table, db.ops, async () => {
      await table.delete(key);
      const op = { ts: Date.now(), ns, table: tableName, key, type: "delete", status: "pending" };
      await db.ops.add(op);
    });
  }, [db, table, ns, tableName]);
  return { list, get, add, put, del: del2, table };
}
function useLocalOps() {
  const { db } = useLocalStorage();
  const listPending = () => db.ops.where("status").equals("pending").toArray();
  const mark = (id, status, error) => db.ops.update(id, { status, error: error ?? null });
  return { listPending, mark };
}

// src/local/sync.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var SyncCtx = createContext4(null);
function LocalSyncProvider({
  adapters,
  children
}) {
  const { listPending, mark } = useLocalOps();
  const flush = useCallback2(async () => {
    const ops = await listPending();
    for (const op of ops) {
      let handled = false;
      let error = null;
      for (const adapter of adapters) {
        try {
          const didHandle = await adapter.apply(op);
          handled = handled || didHandle;
        } catch (err) {
          error = err instanceof Error ? err : new Error(String(err));
          break;
        }
      }
      if (op.id == null) continue;
      if (error) {
        await mark(op.id, "error", error.message);
        continue;
      }
      if (!handled && adapters.length > 0) {
        await mark(op.id, "error", "No storage adapter accepted op");
        continue;
      }
      await mark(op.id, "pushed");
    }
  }, [adapters, listPending, mark]);
  return /* @__PURE__ */ jsx3(SyncCtx.Provider, { value: { flush }, children });
}
function useLocalSync() {
  const ctx = useContext4(SyncCtx);
  if (!ctx) throw new Error("useLocalSync must be used within LocalSyncProvider");
  return ctx;
}

// src/local/registry.ts
var schemaMap = /* @__PURE__ */ new Map();
var factories = /* @__PURE__ */ new Set();
function registerLocalSchema(schema) {
  if (!schema?.ns) {
    throw new Error("Local schemas must provide a stable `ns`.");
  }
  schemaMap.set(schema.ns, schema);
}
function getRegisteredSchemas() {
  return Array.from(schemaMap.values());
}
function registerLocalSyncAdapterFactory(factory) {
  factories.add(factory);
}
function buildRegisteredAdapters(ctx) {
  return Array.from(factories).map((factory) => factory(ctx));
}

// src/useStorageOps.ts
import { useMemo as useMemo3 } from "react";
var normalize = (rel) => String(rel || "").replace(/^\/+/, "");
function useStorageOps() {
  const { s3, cfg, paths, ready, identity } = useStorage();
  return useMemo3(() => {
    const bucket = cfg.bucket;
    const publicBaseUrl = cfg.publicBaseUrl?.replace(/\/+$/, "") || "";
    const assertReady = () => {
      if (!ready || !s3) {
        throw new Error("Storage not ready (user not authenticated yet).");
      }
    };
    const requirePaths = () => {
      if (!paths) throw new Error("Storage paths unavailable");
      return paths;
    };
    const scopedKeyBuilder = (prefixResolver) => (rel) => joinKey(prefixResolver(), normalize(rel));
    const privateKey = scopedKeyBuilder(() => requirePaths().privatePrefix);
    const publicKey = scopedKeyBuilder(() => requirePaths().publicPrefix);
    const sharedKey = scopedKeyBuilder(() => requirePaths().sharedPrefix);
    const makeJsonOps = (keyFn, opts) => ({
      putJson: (rel, data) => {
        assertReady();
        return putJson(s3, bucket, keyFn(rel), data);
      },
      getJson: (rel) => {
        assertReady();
        const key = keyFn(rel);
        if (opts?.useCdn && publicBaseUrl) {
          return getJsonViaCdn(publicBaseUrl, key).catch(() => getJson(s3, bucket, key));
        }
        return getJson(s3, bucket, key);
      },
      getJsonOrNull: (rel, missTtlMs) => {
        assertReady();
        const key = keyFn(rel);
        if (opts?.useCdn && publicBaseUrl) {
          return getJsonOrNullViaCdn(publicBaseUrl, key, missTtlMs).catch((err) => {
            const status = err?.status;
            if (status === 401 || status === 403) {
              return getJsonOrNull(s3, bucket, key, missTtlMs);
            }
            throw err;
          });
        }
        return getJsonOrNull(s3, bucket, key, missTtlMs);
      },
      list: (sub = "") => {
        assertReady();
        return listPrefix(s3, bucket, keyFn(sub));
      },
      delete: (rel) => {
        assertReady();
        return del(s3, bucket, keyFn(rel));
      },
      signGet: (rel, ttlSec = 900) => {
        assertReady();
        return signGetUrl(s3, bucket, keyFn(rel), ttlSec);
      }
    });
    const privateJson = makeJsonOps(privateKey);
    const publicJson = makeJsonOps(publicKey, { useCdn: true });
    const sharedJson = makeJsonOps(sharedKey, { useCdn: true });
    const uploadFile = (keyFn, rel, file, contentType, opts) => {
      assertReady();
      return putFileMultipart(
        s3,
        bucket,
        keyFn(rel),
        file,
        contentType || file.type || "application/octet-stream",
        opts
      );
    };
    const abortForScope = (keyFn, sub = "") => {
      assertReady();
      return abortAllMultipartForPrefix(s3, bucket, keyFn(sub));
    };
    const abortForKey = (keyFn, rel) => {
      assertReady();
      return abortAllMultipartForKey(s3, bucket, keyFn(rel));
    };
    const buildAppPrefix = (appId) => {
      if (!appId || /[\/\\]/.test(appId)) throw new Error("Invalid appId");
      return `platform/apps/${appId}/`;
    };
    const buildAppTenantPrefix = (appId, tenantId) => {
      const tid = tenantId ?? identity?.tenantId;
      if (!tid) throw new Error("TenantId is required for forAppTenant()");
      return `${buildAppPrefix(appId)}tenants/${tid}/`;
    };
    const makeAppStore = (prefix) => {
      const keyFn = (rel) => joinKey(prefix, normalize(rel));
      const jsonOps = makeJsonOps(keyFn);
      return {
        prefix,
        ...jsonOps,
        uploadFile: (rel, file, contentType, opts) => uploadFile(keyFn, rel, file, contentType, opts),
        abortMPUs: (sub = "") => abortForScope(keyFn, sub),
        abortKey: (rel) => abortForKey(keyFn, rel)
      };
    };
    return {
      ready,
      bucket,
      tenantId: identity?.tenantId ?? null,
      // user-private JSON helpers
      putJsonPrivate: privateJson.putJson,
      getJsonPrivate: privateJson.getJson,
      getJsonOrNullPrivate: privateJson.getJsonOrNull,
      listPrivate: privateJson.list,
      deletePrivate: privateJson.delete,
      signPrivateGetUrl: privateJson.signGet,
      uploadPrivateFile: (rel, file, contentType, opts) => uploadFile(privateKey, rel, file, contentType, opts),
      // shared namespace
      putJsonShared: sharedJson.putJson,
      getJsonShared: sharedJson.getJson,
      getJsonOrNullShared: sharedJson.getJsonOrNull,
      listShared: sharedJson.list,
      deleteShared: sharedJson.delete,
      signSharedGetUrl: sharedJson.signGet,
      // public namespace
      putJsonPublic: publicJson.putJson,
      getJsonPublic: publicJson.getJson,
      getJsonOrNullPublic: publicJson.getJsonOrNull,
      listPublic: publicJson.list,
      deletePublic: publicJson.delete,
      signPublicGetUrl: publicJson.signGet,
      uploadPublicFile: (rel, file, contentType, opts) => uploadFile(publicKey, rel, file, contentType, opts),
      abortPublicMPUs: (sub = "") => abortForScope(publicKey, sub),
      abortPrivateMPUs: (sub = "") => abortForScope(privateKey, sub),
      abortMPUsForKey: (rel, scope = "public") => {
        const fn = scope === "public" ? publicKey : scope === "private" ? privateKey : sharedKey;
        return abortForKey(fn, rel);
      },
      app: {
        forApp: (appId) => makeAppStore(buildAppPrefix(appId)),
        forAppTenant: (appId, tenantId) => makeAppStore(buildAppTenantPrefix(appId, tenantId))
      }
    };
  }, [cfg.bucket, cfg.publicBaseUrl, identity?.tenantId, paths, ready, s3]);
}

// src/StorageAppProvider.tsx
import { Fragment, jsx as jsx4 } from "react/jsx-runtime";
var StorageLocalRuntime = ({
  localDbName,
  children
}) => {
  const schemas = useMemo4(() => getRegisteredSchemas(), []);
  const ops = useStorageOps();
  const adapters = useMemo4(() => buildRegisteredAdapters({ ops }), [ops]);
  if (schemas.length === 0 && adapters.length === 0) {
    return /* @__PURE__ */ jsx4(Fragment, { children });
  }
  const withSync = adapters.length > 0 ? /* @__PURE__ */ jsx4(LocalSyncProvider, { adapters, children }) : children;
  return /* @__PURE__ */ jsx4(LocalStorageProvider, { schemas, dbName: localDbName, children: withSync });
};
var StorageAppProvider = ({
  config,
  localDbName,
  children
}) => /* @__PURE__ */ jsx4(StorageProvider, { config, children: /* @__PURE__ */ jsx4(StorageLocalRuntime, { localDbName, children }) });

// src/useS3JsonStore.ts
import { useMemo as useMemo5 } from "react";
var normalize2 = (rel) => String(rel || "").replace(/^\/+/, "");
function useS3JsonStore(options) {
  const ops = useStorageOps();
  const { paths } = useStorage();
  const basePath = normalize2(options.basePath ?? "");
  const extendKey = (rel) => basePath ? joinKey(basePath, normalize2(rel)) : normalize2(rel);
  const prefix = useMemo5(() => {
    const suffix = basePath ? `${basePath}/` : "";
    if (options.kind === "user") {
      if (options.visibility === "public") {
        return paths?.publicPrefix ? joinKey(paths.publicPrefix, suffix) : "";
      }
      return paths?.privatePrefix ? joinKey(paths.privatePrefix, suffix) : "";
    }
    if (options.kind === "shared") {
      return paths?.sharedPrefix ? joinKey(paths.sharedPrefix, suffix) : "";
    }
    if (options.kind === "app") {
      return joinKey(`platform/apps/${options.appId}/`, suffix);
    }
    const tenantId = options.tenantId ?? ops.tenantId ?? "";
    return joinKey(`platform/apps/${options.appId}/tenants/${tenantId}/`, suffix);
  }, [
    basePath,
    ops.tenantId,
    options,
    paths?.privatePrefix,
    paths?.publicPrefix,
    paths?.sharedPrefix
  ]);
  if (options.kind === "user") {
    const isPublic = options.visibility === "public";
    return {
      ready: ops.ready,
      prefix,
      putJson: (rel, data) => (isPublic ? ops.putJsonPublic : ops.putJsonPrivate)(extendKey(rel), data),
      getJson: (rel) => (isPublic ? ops.getJsonPublic : ops.getJsonPrivate)(extendKey(rel)),
      getJsonOrNull: (rel, missTtlMs) => (isPublic ? ops.getJsonOrNullPublic : ops.getJsonOrNullPrivate)(
        extendKey(rel),
        missTtlMs
      ),
      delete: (rel) => (isPublic ? ops.deletePublic : ops.deletePrivate)(extendKey(rel)),
      list: (sub = "") => (isPublic ? ops.listPublic : ops.listPrivate)(extendKey(sub)),
      signGet: (rel, ttlSec = 900) => (isPublic ? ops.signPublicGetUrl : ops.signPrivateGetUrl)(extendKey(rel), ttlSec)
    };
  }
  if (options.kind === "shared") {
    return {
      ready: ops.ready,
      prefix,
      putJson: (rel, data) => ops.putJsonShared(extendKey(rel), data),
      getJson: (rel) => ops.getJsonShared(extendKey(rel)),
      getJsonOrNull: (rel, missTtlMs) => ops.getJsonOrNullShared(extendKey(rel), missTtlMs),
      delete: (rel) => ops.deleteShared(extendKey(rel)),
      list: (sub = "") => ops.listShared(extendKey(sub)),
      signGet: (rel, ttlSec = 900) => ops.signSharedGetUrl(extendKey(rel), ttlSec)
    };
  }
  const appStore = options.kind === "app" ? ops.app.forApp(options.appId) : ops.app.forAppTenant(options.appId, options.tenantId);
  return {
    ready: ops.ready,
    prefix,
    putJson: (rel, data) => appStore.putJson(extendKey(rel), data),
    getJson: (rel) => appStore.getJson(extendKey(rel)),
    getJsonOrNull: (rel, missTtlMs) => appStore.getJsonOrNull(extendKey(rel), missTtlMs),
    delete: (rel) => appStore.delete(extendKey(rel)),
    list: (sub = "") => appStore.list(extendKey(sub)),
    signGet: (rel, ttlSec = 900) => appStore.signGet(extendKey(rel), ttlSec)
  };
}

// src/useTenantPrefix.ts
function useTenantPrefix() {
  const { paths, ready } = useStorage();
  return {
    ready,
    baseUserPrefix: paths?.baseUserPrefix ?? "",
    privatePrefix: paths?.privatePrefix ?? "",
    publicPrefix: paths?.publicPrefix ?? "",
    sharedPrefix: paths?.sharedPrefix ?? ""
  };
}

// src/s3_list.ts
function useListObjects() {
  const { s3, cfg } = useStorage();
  if (!s3 || !cfg?.bucket) {
    return {
      async listByPrefix(_) {
        return { items: [], nextToken: void 0 };
      }
    };
  }
  const listByPrefix = async (opts) => {
    const { ListObjectsV2Command: ListObjectsV2Command2 } = await import("@aws-sdk/client-s3");
    const resp = await s3.send(
      new ListObjectsV2Command2({
        Bucket: cfg.bucket,
        Prefix: opts.prefix,
        ContinuationToken: opts.continuationToken,
        MaxKeys: opts.maxKeys ?? 1e3
      })
    );
    const items = (resp.Contents || []).filter((o) => o.Key && o.Key !== opts.prefix).map((o) => ({
      key: o.Key,
      size: o.Size,
      lastModified: o.LastModified?.toISOString()
    })) ?? [];
    return {
      items,
      nextToken: resp.IsTruncated ? resp.NextContinuationToken : void 0
    };
  };
  return { listByPrefix };
}

// src/cdn.ts
function useCdnUrls() {
  const { cfg } = useStorage();
  const base = (cfg?.publicBaseUrl || "").replace(/\/+$/, "");
  const toUrl = (key) => `${base}/${encodeURI(key.replace(/^\/+/, ""))}`;
  return { toUrl };
}

// src/cf_signer.ts
import { useAuth as useAuth2 } from "@coremind/identity-core";
function useCfSigner() {
  const { cfg } = useStorage();
  const { session } = useAuth2();
  async function signPrivateCfUrl(relKey, ttlSec) {
    if (!cfg?.signerBaseUrl) throw new Error("signerBaseUrl not configured");
    const r = await fetch(`${cfg.signerBaseUrl.replace(/\/+$/, "")}/storage/cf-sign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...session?.token ? { Authorization: `Bearer ${session.token}` } : {}
      },
      body: JSON.stringify({ key: relKey, ttl: ttlSec }),
      // rel to private prefix; server adds tenant
      credentials: "include"
      // if your API uses cookies
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || r.statusText);
    return data.url;
  }
  return { signPrivateCfUrl };
}

// src/s3ProfileStore.ts
import {
  S3Client as S3Client2,
  PutObjectCommand as PutObjectCommand2,
  GetObjectCommand as GetObjectCommand2,
  HeadObjectCommand
} from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool as fromCognitoIdentityPool2 } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { StandardRetryStrategy } from "@smithy/middleware-retry";
var CACHE_TTL_MS = 6e4;
var userCache = /* @__PURE__ */ new Map();
var profileCache = /* @__PURE__ */ new Map();
var readCache = (map, key) => {
  const entry = map.get(key);
  if (!entry) return void 0;
  if (Date.now() > entry.expiresAt) {
    map.delete(key);
    return void 0;
  }
  return entry.value;
};
var writeCache = (map, key, value) => {
  map.set(key, {
    value,
    expiresAt: Date.now() + CACHE_TTL_MS
  });
};
async function userKey(cfg, sub) {
  const tenant = await cfg.getTenantId();
  return `${cfg.platform}/tenants/${tenant}/users/${sub}/private/user.json`;
}
async function profileKey(cfg, sub) {
  const tenant = await cfg.getTenantId();
  return `${cfg.platform}/tenants/${tenant}/users/${sub}/private/profile.json`;
}
var cached = null;
function makeKey(cfg, tenantId, sub, idToken) {
  return [
    cfg.identityPoolId,
    cfg.region,
    cfg.userPoolId,
    tenantId,
    sub,
    idToken.slice(0, 24)
    // short prefix is enough
  ].join("|");
}
async function getS3(cfg) {
  const tenantId = await Promise.resolve(cfg.getTenantId());
  const idToken = cfg.getIdToken?.() ?? "";
  const sub = parseJwtSub(idToken) ?? "anon";
  const key = makeKey(cfg, tenantId, sub, idToken);
  if (cached?.key === key) return cached.s3;
  const loginsKey = `cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`;
  const identityRegion = cfg.identityPoolRegion || cfg.region;
  const cognitoIdentityClient = new CognitoIdentityClient({ region: identityRegion });
  const retryStrategy = new StandardRetryStrategy(() => Promise.resolve(8));
  const s3 = new S3Client2({
    region: cfg.region,
    credentials: fromCognitoIdentityPool2({
      identityPoolId: cfg.identityPoolId,
      client: cognitoIdentityClient,
      // ✅ correct name
      logins: idToken ? { [loginsKey]: idToken } : void 0
    }),
    maxAttempts: 8,
    retryStrategy
  });
  cached = { key, s3 };
  return s3;
}
function parseJwtSub(token) {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    const data = JSON.parse(atob(payload));
    return data?.sub ?? null;
  } catch {
    return null;
  }
}
var inflight = /* @__PURE__ */ new Map();
function coalesce(key, fn) {
  const existing = inflight.get(key);
  if (existing) return existing;
  const run = fn().finally(() => inflight.delete(key));
  inflight.set(key, run);
  return run;
}
function createS3ProfileStore(cfg) {
  return {
    // ----- user.json (system metadata) -----
    async getUser(sub) {
      const s3 = await getS3(cfg);
      const Key = await userKey(cfg, sub);
      const cached2 = readCache(userCache, Key);
      if (cached2 !== void 0) {
        return cached2;
      }
      return coalesce(`GET:${Key}`, async () => {
        try {
          const out = await s3.send(new GetObjectCommand2({ Bucket: cfg.bucket, Key, ResponseCacheControl: "no-cache" }));
          const text = await out.Body.transformToString();
          const parsed = JSON.parse(text);
          writeCache(userCache, Key, parsed);
          return parsed;
        } catch (err) {
          if (err?.$metadata?.httpStatusCode === 404 || err?.name === "NoSuchKey") {
            writeCache(userCache, Key, null);
            return null;
          }
          throw err;
        }
      });
    },
    async userExists(sub) {
      const s3 = await getS3(cfg);
      const Key = await userKey(cfg, sub);
      try {
        await s3.send(new HeadObjectCommand({ Bucket: cfg.bucket, Key }));
        return true;
      } catch (err) {
        if (err?.$metadata?.httpStatusCode === 404 || err?.name === "NotFound") return false;
        throw err;
      }
    },
    async putUser(sub, user) {
      const s3 = await getS3(cfg);
      const Key = await userKey(cfg, sub);
      return coalesce(`PUT:${Key}`, async () => {
        await s3.send(new PutObjectCommand2({
          Bucket: cfg.bucket,
          Key,
          Body: JSON.stringify(user),
          ContentType: "application/json",
          ServerSideEncryption: "AES256",
          CacheControl: "no-cache, no-store, must-revalidate",
          Expires: /* @__PURE__ */ new Date(0)
        }));
        writeCache(userCache, Key, user);
      });
    },
    // ----- profile.json (user editable) -----
    async getProfile(sub) {
      const s3 = await getS3(cfg);
      const Key = await profileKey(cfg, sub);
      const cached2 = readCache(profileCache, Key);
      if (cached2 !== void 0) {
        return cached2;
      }
      return coalesce(`GET:${Key}`, async () => {
        try {
          const out = await s3.send(new GetObjectCommand2({ Bucket: cfg.bucket, Key, ResponseCacheControl: "no-cache" }));
          const text = await out.Body.transformToString();
          const parsed = JSON.parse(text);
          writeCache(profileCache, Key, parsed);
          return parsed;
        } catch (err) {
          if (err?.$metadata?.httpStatusCode === 404 || err?.name === "NoSuchKey") {
            writeCache(profileCache, Key, null);
            return null;
          }
          throw err;
        }
      });
    },
    async putProfile(sub, profile) {
      const s3 = await getS3(cfg);
      const Key = await profileKey(cfg, sub);
      return coalesce(`PUT:${Key}`, async () => {
        await s3.send(new PutObjectCommand2({
          Bucket: cfg.bucket,
          Key,
          Body: JSON.stringify(profile),
          // still JSON on the wire
          ContentType: "application/json",
          ServerSideEncryption: "AES256",
          CacheControl: "no-cache, no-store, must-revalidate",
          Expires: /* @__PURE__ */ new Date(0)
        }));
        writeCache(profileCache, Key, profile);
      });
    }
  };
}

// src/browserStore.ts
var normalizePrefix = (prefix) => String(prefix ?? "").trim().replace(/:+$/, "");
var getDefaultStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};
function makeMemoryStorage() {
  const map = /* @__PURE__ */ new Map();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
    keys: () => Array.from(map.keys())
  };
}
function wrapStorage(storage) {
  if (!storage) return makeMemoryStorage();
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => storage.setItem(key, value),
    removeItem: (key) => storage.removeItem(key),
    keys: () => {
      const keys = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key) keys.push(key);
      }
      return keys;
    }
  };
}
function createBrowserJsonStore(options = {}) {
  const prefix = normalizePrefix(options.prefix);
  const target = wrapStorage(options.storage ?? getDefaultStorage());
  const buildKey = (rel) => {
    const normalized = String(rel ?? "").trim().replace(/^:+/, "");
    return prefix ? `${prefix}:${normalized}` : normalized;
  };
  const read = async (key) => {
    try {
      const raw = target.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  return {
    ready: true,
    prefix,
    putJson: async (rel, data) => {
      target.setItem(buildKey(rel), JSON.stringify(data ?? null));
    },
    delete: async (rel) => {
      target.removeItem(buildKey(rel));
    },
    getJson: (rel) => read(buildKey(rel)),
    getJsonOrNull: (rel) => read(buildKey(rel)),
    list: async () => {
      const keys = target.keys().filter((k) => prefix ? k.startsWith(`${prefix}:`) : true);
      const rows = [];
      for (const key of keys) {
        const data = await read(key);
        rows.push({ key, data });
      }
      return rows;
    }
  };
}

// src/sync/useSyncJsonDoc.ts
import { useEffect as useEffect2, useRef } from "react";

// src/sync/jsonDocSync.ts
var toEpochMs = (raw) => raw == null ? void 0 : typeof raw === "number" ? raw < 1e12 ? raw * 1e3 : raw : /^\d+$/.test(String(raw)) ? Number(raw) < 1e12 ? Number(raw) * 1e3 : Number(raw) : ((t) => isNaN(t) ? void 0 : t)(Date.parse(String(raw)));
async function pullAndMaybeReconcile({
  user,
  endpoints,
  local,
  fetchImpl = fetch,
  reconcile
}) {
  const url = endpoints.buildUrl(user);
  const res = await fetchImpl(`${url}?t=${Date.now()}`, { cache: "no-store", headers: { Accept: "application/json" } });
  if (!res.ok) return { changed: false };
  const raw = await res.json();
  const remoteDoc = endpoints.decode ? endpoints.decode(raw) : raw;
  if (!remoteDoc || typeof remoteDoc.version !== "number") return { changed: false };
  const { version: rVer, updated_at } = remoteDoc;
  const rUpdated = toEpochMs(updated_at);
  const meta = await local.readMeta();
  const isNewer = (rVer ?? 0) > (meta.version ?? 0) || (rUpdated ?? 0) > (meta.updatedAt ?? 0);
  if (!isNewer) return { changed: false };
  const localData = await local.loadLocal();
  const merged = reconcile ? reconcile({ local: localData, remote: remoteDoc.data }) : remoteDoc.data;
  await local.saveLocal(merged);
  await local.writeMeta({ version: rVer, updatedAt: rUpdated });
  return { changed: true, doc: { ...remoteDoc, data: merged } };
}
async function pushWithBump({
  user,
  endpoints,
  local,
  fetchImpl = fetch,
  nextData
}) {
  const meta = await local.readMeta();
  const nextDoc = {
    version: (meta.version ?? 0) + 1,
    updated_at: Date.now(),
    data: nextData
  };
  const body = endpoints.encode ? endpoints.encode(nextDoc) : nextDoc;
  const url = endpoints.buildUrl(user);
  const r = await fetchImpl(url, { method: "PUT", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } });
  if (!r.ok) throw new Error(`PUT failed: ${r.status}`);
  await local.saveLocal(nextData);
  await local.writeMeta({ version: nextDoc.version, updatedAt: toEpochMs(nextDoc.updated_at) });
  return nextDoc;
}

// src/sync/useSyncJsonDoc.ts
function useSyncJsonDoc({
  enabled,
  user,
  endpoints,
  local,
  setState,
  reconcile
}) {
  const ran = useRef(false);
  useEffect2(() => {
    if (!enabled || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await pullAndMaybeReconcile({ user, endpoints, local, reconcile });
        setState(await local.loadLocal());
      } catch (e) {
      }
    })();
  }, [enabled, user?.sub, user?.username, user?.email]);
}

// src/sync/localDexieAdapter.ts
var metaKey = (ns) => `${ns}:meta`;
function makeLocalJsonAdapter(ns, io) {
  return {
    async loadLocal() {
      return io.read();
    },
    async saveLocal(next) {
      await io.write(next);
    },
    async readMeta() {
      try {
        const raw = localStorage.getItem(metaKey(ns));
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    },
    async writeMeta(meta) {
      localStorage.setItem(metaKey(ns), JSON.stringify(meta));
    }
  };
}
export {
  LocalStorageProvider,
  LocalSyncProvider,
  StorageAppProvider,
  StorageConfigContext,
  StorageProvider,
  buildRegisteredAdapters,
  createBrowserJsonStore,
  createS3ProfileStore,
  getRegisteredSchemas,
  joinKey,
  makeLocalJsonAdapter,
  pushWithBump,
  registerLocalSchema,
  registerLocalSyncAdapterFactory,
  useCdnUrls,
  useCfSigner,
  useListObjects,
  useLocalCollection,
  useLocalSync,
  useS3JsonStore,
  useStorage,
  useStorageConfig,
  useStorageOps,
  useSyncJsonDoc,
  useTenantPrefix
};
